import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@saas-kit/lib';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorBanner,
  Field,
  InputField,
  MultiSelectChips,
  toast,
} from '@saas-kit/ui';

import { createApiKey, type ApiKeyCreated, type PermissionScope } from './api';

interface Props {
  scopes: PermissionScope[];
  scopesError: unknown;
  onCreated: (created: ApiKeyCreated) => void;
}

export function ApiKeyCreateForm({
  scopes,
  scopesError,
  onCreated,
}: Props): JSX.Element {
  const { t } = useTranslation('apiKeys');
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: createApiKey,
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['tenant-api-keys'] });
      setName('');
      setSelected([]);
      toast.success(t('toasts.created'));
      onCreated(created);
    },
    onError: (error) => toast.error(t(parseApiError(error).i18nKey)),
  });

  const availableScopes = scopes
    .filter((scope) => scope.assignable)
    .map((scope) => ({ value: scope.key, label: scope.key }));

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!name.trim()) return;
    mutation.mutate({ name: name.trim(), scopes: selected });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('create.title')}</CardTitle>
        <CardDescription>{t('create.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form className="space-y-4" onSubmit={onSubmit}>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
            <InputField
              label={t('create.name_label')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('create.name_placeholder')}
            />
            <Field
              label={t('create.scopes_label')}
              hint={t('create.scopes_hint')}
            >
              <MultiSelectChips
                options={availableScopes}
                value={selected}
                onChange={setSelected}
                label={t('create.scopes_picker')}
              />
            </Field>
          </div>

          {scopesError ? (
            <ErrorBanner>{t(parseApiError(scopesError).i18nKey)}</ErrorBanner>
          ) : null}

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!name.trim() || mutation.isPending}
            >
              {mutation.isPending
                ? t('create.submitting')
                : t('create.submit')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
